import React, { memo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import RemoveIcon from 'transfix-icons/build/RemoveIcon'
import { Button } from 'transfix-ui/components/Button'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import {
  IMotorCarrier,
  IUserMotorCarrier,
  IMotorCarrierApplication,
} from 'apps/fleet-planner/types'
import FORMATS from 'constants/datetime'
import { formatState } from './CarriersTableRow'

interface ICarrierStatusTagProps {
  carrierApplication: IMotorCarrierApplication
}

const CarrierStatusTag = memo<ICarrierStatusTagProps>(({ carrierApplication }) => {
  const applicationStatus = formatState(carrierApplication)
  const tagStyle = applicationStatus === 'Activated' ? TAG_STYLE.success : TAG_STYLE.dark
  return <Tag label={applicationStatus as string} tagStyle={tagStyle} size={TAG_SIZE.small} />
})

interface ICarriersMobileRow {
  carrier: IUserMotorCarrier
  onRemove: (motorCarrier: IMotorCarrier) => void
}

const useCarriersMobileRowStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${brandColors.coolGray1}`,
  },
}))

export const CarriersMobileRow = memo<ICarriersMobileRow>(({ carrier, onRemove }) => {
  const { carrierApplication, membership, motorCarrier } = carrier || {}
  const { dotNumber, mcNumber, name } = motorCarrier || {}
  const { createdAt, usersCount } = membership || {}
  const classes = useCarriersMobileRowStyles()

  const removeMcHandler = useCallback(() => onRemove(motorCarrier), [onRemove, motorCarrier])

  return (
    <Box className={classes.root} data-test='facilities-mobile-row'>
      <Box>
        <Grid
          container
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          wrap='nowrap'>
          <Grid item xs={6}>
            <Typography variant='subtitle2'>{name}</Typography>
          </Grid>
          {carrierApplication && (
            <Grid item xs={6} style={{ textAlign: 'left' }}>
              <CarrierStatusTag carrierApplication={carrierApplication} />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box>
        <Typography variant='button' component='span'>
          DOT
        </Typography>{' '}
        #{dotNumber}
      </Box>
      <Box>
        <Typography variant='button' component='span'>
          MC
        </Typography>{' '}
        {mcNumber ? `#${mcNumber}` : 'N/A'}
      </Box>
      <Box component='p' pt={1}>
        <Typography variant='button'>
          {!usersCount || usersCount - 1 <= 1 ? (
            'No team members'
          ) : (
            <Link to='team'>{usersCount - 1} Team Members</Link>
          )}
        </Typography>
      </Box>
      <Box>
        <Grid
          container
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          wrap='nowrap'>
          <Grid item xs={6}>
            <Typography variant='button' component='span'>
              Added
            </Typography>{' '}
            {moment(createdAt).format(FORMATS.readableFormat)}
          </Grid>
          <Grid item xs={6}>
            {!!carrierApplication?.state && (
              <Button color='tertiary' onClick={removeMcHandler} startIcon={<RemoveIcon />}>
                Remove Carrier
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
})
